import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { MagnifyingGlass, X } from '@phosphor-icons/react'
import { contactsApi } from '@/lib/api'

interface TagOption {
  name: string
  count: number
}

interface TagAutocompleteProps {
  selectedTags: string[]
  onChange: (tags: string[]) => void
}

/** Max height of the suggestion menu in px — the old `max-h-48`. */
const MENU_MAX_HEIGHT = 192

export default function TagAutocomplete({ selectedTags, onChange }: TagAutocompleteProps) {
  const [allTags, setAllTags] = useState<TagOption[]>([])
  const [loadFailed, setLoadFailed] = useState(false)
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // A silent `.catch(() => setLoaded(true))` left `allTags` empty forever with
  // no retry, so "the request failed" was indistinguishable from "no tags exist
  // yet". Track the failure and surface it in the menu instead.
  const fetchTags = useCallback(() => {
    setLoadFailed(false)
    contactsApi.getTags()
      .then(res => setAllTags(res.data))
      .catch(() => setLoadFailed(true))
  }, [])

  // Fetch tags once on mount
  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  const closeMenu = useCallback(() => {
    setIsOpen(false)
    setQuery('')
    setSelectedIdx(0)
  }, [])

  const filtered = allTags.filter(t =>
    t.name.toLowerCase().includes(query.toLowerCase()) &&
    !selectedTags.includes(t.name)
  )

  const queryTrimmed = query.trim().toLowerCase()
  const isNewTag = queryTrimmed.length > 0 &&
    !allTags.some(t => t.name.toLowerCase() === queryTrimmed) &&
    !selectedTags.some(t => t.toLowerCase() === queryTrimmed)

  const totalOptions = filtered.length + (isNewTag ? 1 : 0)
  // A failed fetch is worth a menu of its own — otherwise the retry affordance
  // has nowhere to live.
  const showMenu = isOpen && (totalOptions > 0 || loadFailed)

  const addTag = useCallback((tag: string) => {
    const trimmed = tag.trim()
    if (!trimmed || selectedTags.includes(trimmed)) return
    onChange([...selectedTags, trimmed])
    setQuery('')
    setSelectedIdx(0)
    inputRef.current?.focus()
  }, [selectedTags, onChange])

  const removeTag = useCallback((tag: string) => {
    onChange(selectedTags.filter(t => t !== tag))
  }, [selectedTags, onChange])

  // Mirrors `Select.computeMenuStyle` — fixed coords off the input's rect so the
  // portalled menu escapes every `overflow-*` ancestor (§19), clamped to the
  // viewport on both axes and flipped upward when below is cramped.
  const computeMenuStyle = useCallback((): React.CSSProperties | null => {
    const rect = inputRef.current?.getBoundingClientRect()
    if (!rect) return null
    const margin = 6
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    const spaceBelow = viewportH - rect.bottom - margin
    const spaceAbove = rect.top - margin
    // Open upward only when below is cramped AND above has more room.
    const openUp = spaceBelow < Math.min(MENU_MAX_HEIGHT, 200) && spaceAbove > spaceBelow
    const available = Math.max(0, openUp ? spaceAbove : spaceBelow)
    const maxHeight = Math.min(MENU_MAX_HEIGHT, available)
    const left = Math.max(margin, Math.min(rect.left, viewportW - rect.width - margin))
    const base: React.CSSProperties = {
      position: 'fixed',
      left,
      width: rect.width,
      maxHeight,
      // Numeric, not a Tailwind `z-*` class: this node is a direct child of
      // <body>, and 1000 matches the rest of the portal layer (Select).
      zIndex: 1000,
    }
    // Anchor the BOTTOM edge when opening up so a short menu hugs the input
    // instead of floating detached above it.
    if (openUp) {
      base.bottom = viewportH - rect.top + margin
    } else {
      base.top = rect.bottom + margin
    }
    return base
  }, [])

  // Position on open, then dismiss on scroll + resize (§54). `selectedTags.length`
  // is a dependency because adding or removing a chip re-flows the chip row above
  // the input and therefore moves the anchor.
  useLayoutEffect(() => {
    if (!showMenu) {
      setMenuStyle(null)
      return
    }
    setMenuStyle(computeMenuStyle())
    const onScroll = (e: Event) => {
      // The menu's own `overflow-y-auto` list must not dismiss the menu it
      // belongs to — `scroll` does not bubble, so capture sees it here.
      if (menuRef.current?.contains(e.target as Node)) return
      closeMenu()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', closeMenu)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', closeMenu)
    }
  }, [showMenu, computeMenuStyle, closeMenu, selectedTags.length])

  // Close on outside click — must check the portalled menu too, since it is no
  // longer a DOM descendant of the container.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      closeMenu()
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [isOpen, closeMenu])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx(i => Math.min(i + 1, totalOptions - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (totalOptions === 0) return
      if (selectedIdx < filtered.length) {
        addTag(filtered[selectedIdx].name)
      } else if (isNewTag) {
        addTag(query.trim())
      }
    } else if (e.key === 'Escape') {
      // Only swallow Escape when there is actually something to dismiss.
      // Otherwise it must reach the enclosing modal so that still closes —
      // this used to clear the query AND propagate, firing both.
      if (!showMenu && query === '') return
      e.preventDefault()
      e.stopPropagation()
      closeMenu()
    } else if (e.key === 'Backspace' && query === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1])
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedTags.map(tag => {
            const exists = allTags.some(t => t.name === tag)
            return (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] ${
                  exists ? '' : 'border border-dashed border-[#5e6ad2]/30 dark:border-[#5e6ad2]/40'
                }`}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] ml-0.5 transition-colors duration-150"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )
          })}
        </div>
      )}

      {/* MagnifyingGlass input */}
      <div className="relative">
        <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground dark:text-[#8a8f98]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
            setSelectedIdx(0)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search or create tag..."
          className="w-full h-9 pl-8 pr-3 bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] rounded-md text-[13px] text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 transition-colors duration-150"
        />
      </div>

      {/* Dropdown — portalled to <body> so neither the AddContactModal form nor
          the NodeConfigPanel scroller can clip it (§19). */}
      {createPortal(
        <AnimatePresence>
          {showMenu && menuStyle && (
            <motion.div
              initial={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              style={menuStyle}
              className={menuStyle.bottom != null ? 'origin-bottom' : 'origin-top'}
            >
              <div
                ref={menuRef}
                style={{ maxHeight: menuStyle.maxHeight }}
                className="overflow-y-auto bg-card border border-border/60 rounded-lg shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]"
              >
                {loadFailed && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="px-3 py-2 text-[11px] leading-snug text-muted-foreground dark:text-[#8a8f98]"
                  >
                    Could not load existing tags.{' '}
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); fetchTags(); inputRef.current?.focus() }}
                      className="font-medium text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline transition-colors duration-150"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {filtered.map((tag, i) => (
                  <button
                    key={tag.name}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); addTag(tag.name) }}
                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between transition-colors duration-150 ${
                      i === selectedIdx
                        ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-white/[0.06] dark:text-[#a5b4fc]'
                        : 'text-foreground hover:bg-muted/50 dark:text-[#f7f8f8] dark:hover:bg-white/[0.04]'
                    }`}
                  >
                    <span>{tag.name}</span>
                    <span className="text-[10px] text-muted-foreground dark:text-[#62666d]">{tag.count} contacts</span>
                  </button>
                ))}
                {isNewTag && (
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); addTag(query.trim()) }}
                    className={`w-full text-left px-3 py-1.5 text-sm border-t border-border/50 dark:border-white/[0.06] transition-colors duration-150 ${
                      selectedIdx === filtered.length
                        ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-white/[0.06] dark:text-[#a5b4fc]'
                        : 'text-[#5e6ad2] dark:text-[#a5b4fc] hover:bg-muted/50 dark:hover:bg-white/[0.04]'
                    }`}
                  >
                    + Create "{query.trim()}"
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}
